import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { useLeads } from '@/features/leads/hooks/useLeads'
import { useCrmSettings } from './hooks/useCrmSettings'
import {
  savePipeline,
  saveTemplate,
  saveCompany,
  subscribeToCompany,
} from './repository/settingsRepository'
import { EMPTY_COMPANY, type CompanyDetails } from '@/types/company'
import { StageEditor } from './components/StageEditor'
import { TemplateEditor } from './components/TemplateEditor'
import { CompanyEditor } from './components/CompanyEditor'
import './AdminView.css'

type Tab = 'pipeline' | 'templates' | 'company'

const TABS: { key: Tab; label: string; lede: string }[] = [
  {
    key: 'pipeline',
    label: 'Pipeline stages',
    lede: 'Rename, recolour and reorder the stages a lead moves through, and choose which are held back from the working list. Changes apply live to every open workspace.',
  },
  {
    key: 'templates',
    label: 'Email templates',
    lede: 'The bilingual outreach suggestion shown on each lead, per stage. Prospect splits in two on whether anyone at the company already uses the consumer app. Names in square brackets are merge fields — they are filled in from the lead and from you before the email opens.',
  },
  {
    key: 'company',
    label: 'Company details',
    lede: 'The facts a formal document has to state: the legal name and organisation number at the head of it, the account a payment request asks investors to pay into, the par value the share capital is computed from, and the board that signs the minutes. Entered once here rather than typed into each document.',
  },
]

/**
 * Screen 3 of the design: process & template settings.
 *
 * The two editors were side by side, which gave the template editor — two
 * languages, four fields each — about half the width it wants, and the stage
 * list far more than it needs. They are separate jobs anyway: nobody renames a
 * stage and rewrites an email in the same sitting. One tab each.
 *
 * Team used to be the third tab. The 13 Aug round gave it a home of its own
 * under Internal, and it is one screen in one place rather than two addresses
 * for one list — see `features/settings/TeamScreen.tsx`.
 *
 * Read-only for non-admins — the same restriction the Firestore rules enforce,
 * surfaced in the UI so a sales rep sees why the controls are inert.
 */
export function AdminView({ onBack }: { onBack: () => void }) {
  const { user, isAdmin } = useAuth()
  const settings = useCrmSettings()
  const { leads } = useLeads()
  const [tab, setTab] = useState<Tab>('pipeline')
  const [company, setCompany] = useState<CompanyDetails>(EMPTY_COMPANY)

  useEffect(() => subscribeToCompany(setCompany, () => setCompany(EMPTY_COMPANY)), [])

  // A stage with leads assigned can't be deleted — computed here rather than
  // hard-coded, so it stays true as leads move.
  const usedKeys = useMemo(() => new Set(leads.map((l) => l.stage)), [leads])

  const active = TABS.find((t) => t.key === tab)!

  return (
    <div className="admin">
      <div className="admin__head">
        <div>
          <span className="eyebrow">03 · Admin</span>
          <h1 className="page-title">Process &amp; template settings</h1>
          <p className="page-lede">
            {active.lede}
            {!isAdmin && ' Your account has read-only access to these settings.'}
          </p>
        </div>
        <button className="btn btn--ghost admin__back" onClick={onBack}>
          ← Back to leads
        </button>
      </div>

      <div className="admintabs" role="tablist" aria-label="Settings sections">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`admintabs__tab${tab === t.key ? ' admintabs__tab--active' : ''}`}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {settings.error && <div className="admin__error">{settings.error}</div>}

      <div className="admin__panel">
        {tab === 'pipeline' && (
          <StageEditor
            stages={settings.stages}
            outcomes={settings.outcomes}
            usedKeys={usedKeys}
            readOnly={!isAdmin}
            onSave={(stages, outcomes) =>
              savePipeline({ stages, outcomes }, user?.email ?? 'unknown')
            }
          />
        )}
        {tab === 'company' && (
          <CompanyEditor
            company={company}
            readOnly={!isAdmin}
            onSave={(c) => saveCompany(c, user?.email ?? 'unknown')}
          />
        )}
        {tab === 'templates' && (
          <TemplateEditor
            stages={settings.stages}
            outcomes={settings.outcomes}
            templates={settings.templates}
            readOnly={!isAdmin}
            onSave={(key, template) => saveTemplate(key, template, user?.email ?? 'unknown')}
          />
        )}
      </div>
    </div>
  )
}
